import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Toast } from '../components/ui/Toast';
import { 
  Clock, 
  Save, 
  RotateCcw,
  CheckCircle,
  Users,
  GraduationCap,
  Settings as SettingsIcon,
  Info,
  AlertTriangle,
  AlertCircle,
  Calendar,
  Bell,
  FileText,
  Shield,
  Loader2,
  Coffee,
  ClipboardList,
  Plus,
  Trash2,
  Edit,
  Search,
  X,
  IdCard,
} from 'lucide-react';
import { api } from '../services/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import ChargementSection from './parametrage/ChargementSection';
import ChampSelect from './ui/ChampSelect';
import CarteReglages from './parametrage/CarteReglages';
// L'onglet des badges vit dans son propre fichier, et n'est monté qu'ici : ce
// fichier-ci sert tous les jours, et l'inscription d'un onglet de lecture
// seule ne vaut pas d'y ajouter trois cents lignes de plus.
import BadgesRfid from './parametrage/BadgesRfid';
import RapportsPointage from './parametrage/rapportsPointage/RapportsPointage';
import ConfirmDialog from './ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

/**
 * Les deux boutons d'enregistrement d'un onglet, au format d'en-tete de
 * section (PARAMETRAGE-MODULES.md §5).
 *
 * `border border-transparent` sur le bouton plein : sans elle il ferait 32 px
 * quand son voisin borde en fait 34. Une bordure ajoute un pixel en haut et un
 * en bas, et deux boutons du meme geste doivent avoir la meme taille.
 */
const BoutonsEnregistrer = ({ accent, hasChanges, saving, onReset, onSave }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={onReset}
      disabled={!hasChanges || saving}
      className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <RotateCcw size={13} /> Réinitialiser
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={!hasChanges || saving}
      className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
        hasChanges ? 'hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'
      }`}
      style={hasChanges ? { backgroundColor: accent } : undefined}
    >
      {saving
        ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        : <Save size={13} />}
      Enregistrer
    </button>
  </div>
);

const ParametresPointage = ({ onBackToDashboard }) => {
  // ============================================
  // ÉTATS GÉNÉRAUX
  // ============================================
  const [activeTab, setActiveTab] = useState('horaires-fixes');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);


  // Etas des jours spéciaux 
  const [jourSpecialTemp, setJourSpecialTemp] = useState({
  jour: '',
  heure_fin: '',
  // Facultatifs : vides, le jour suit la pause habituelle du groupe.
  pause_debut: '',
  pause_duree_minutes: ''
});

  // États Toast
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  // ============================================
  // ÉTATS PARAMÈTRES (Onglets 1, 2, 3)
  // ============================================
  const [parametres, setParametres] = useState({
    // Horaires Fixes
    //
    // `arrivee_heure_prevue` et `depart_heure_prevue` sont les heures que
    // l'employe doit respecter : ce sont elles qui decident du retard. Elles
    // existaient en base et aucun ecran ne les ecrivait — elles valaient 07:30
    // et 17:00 en production, une valeur posee a la creation de la ligne que
    // personne n'avait choisie.
    // « Vendredi, tout l'etablissement finit a 14h ». Meme forme que celle des
    // groupes horaires — { vendredi: { heure_fin: '14:00:00' } } — et les
    // groupes peuvent la surcharger.
    jours_speciaux: {},
    arrivee_heure_prevue: '07:30:00',
    depart_heure_prevue: '17:00:00',
    arrivee_debut: '06:00:00',
    arrivee_fin: '10:00:00',
    arrivee_tolerance_minutes: 20,
    fixe_tolerance_debut_avant_minutes: 15,
    fixe_tolerance_debut_apres_minutes: 20,
    depart_debut: '15:00:00',
    depart_fin: '23:00:00',
    depart_tolerance_minutes: 10,
    fixe_tolerance_fin_avant_minutes: 10,
    fixe_tolerance_fin_apres_minutes: 10,
    mode_pause: 'automatique',
    // `pause_duree_auto` est un TIME en base ('01:00:00'). L'ecran le manipule
    // en minutes — « 60 minutes de pause » se saisit mieux que « 01:00:00 » —
    // et la conversion se fait a l'enregistrement.
    pause_duree_auto: '01:00:00',
    // Vide = aucune deduction. Tant qu'aucune date n'est posee, les journees
    // gardent la duree qu'elles avaient : c'est ce qui protege un mois de paie
    // deja entame le jour ou l'ecole active la deduction.
    pause_deduction_date_effet: '',
    // L'heure a laquelle la pause commence. La fin s'en deduit : debut + duree.
    // Les quatre fenetres qui servaient avant — pause_sortie_debut/fin,
    // pause_retour_debut/fin — ne sont plus lues : elles ne repondaient pas a
    // « a quelle heure la pause commence-t-elle ? », et c'est la seule question
    // qui compte pour situer les deux badges.
    pause_debut_prevue: '12:00:00',
    pause_duree_prevue_minutes: 60,
    pause_tolerance_minutes: 10,
    
    // Horaires Variables
    tolerance_debut_avant_minutes: 15,
    tolerance_debut_apres_minutes: 15,
    tolerance_fin_avant_minutes: 15,
    tolerance_fin_apres_minutes: 15,
    heures_supplementaires_actives: true,
    heures_supplementaires_bloquantes: true,
    sortie_anticipee_bloquante: true,
    retard_bloquant: true,
    debut_trop_tot_bloquant: false,
    anomalies_bloquantes: true

    // L'onglet « Regles avancees » est parti le 12 septembre 2026, avec ses dix
    // reglages : notifications de retard, validation automatique, format et
    // frequence des rapports, verrouillage, tracabilite des modifications.
    //
    // Tous etaient enregistres en base et relus par personne. L'audit l'a
    // verifie un par un : aucun code metier ne les lit, et la production
    // portait `verrouillage_jours = 7` et `logs_modifications = 1` — des
    // valeurs qui avaient l'air de regler quelque chose.
    //
    // Les colonnes restent en base : les retirer demande une migration sur
    // chaque ecole, et elles ne genent personne tant qu'aucun ecran ne les
    // montre.
  });

  const [initialParametres, setInitialParametres] = useState(null);

  // ============================================
  // ÉTATS GESTION HORAIRES (Onglet 4)
  // ============================================
  const [groupes, setGroupes] = useState([]);
  const [groupeSelectionne, setGroupeSelectionne] = useState(null);
  const [employesNonAffectes, setEmployesNonAffectes] = useState([]);
  const [employesGroupe, setEmployesGroupe] = useState([]);
  const [employesSelectionnes, setEmployesSelectionnes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModalGroupe, setShowModalGroupe] = useState(false);
  // `window.confirm` bloquait le navigateur et sortait du systeme de dialogues
  // de l'application. CLAUDE.md demande `ConfirmDialog`.
  const [groupeASupprimer, setGroupeASupprimer] = useState(null);
  const [nouveauGroupe, setNouveauGroupe] = useState({
    nom: '',
    description: '',
    heure_debut: '08:00:00',
    heure_fin: '17:00:00',
    tolerance_arrivee_minutes: 20,
    tolerance_depart_minutes: 10,
          tolerance_debut_avant_minutes: 15,
          tolerance_debut_apres_minutes: 20,
          tolerance_fin_avant_minutes: 10,
          tolerance_fin_apres_minutes: 10,
    couleur: '#3084b5',
    jours_travailles: ['lundi','mardi','mercredi','jeudi','vendredi']
  });

  // ============================================
  // FONCTIONS UTILITAIRES
  // ============================================
  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
  };

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  useEffect(() => {
    loadParametres();
    loadGroupes();
    loadEmployesNonAffectes();
  }, []);

  const loadParametres = async () => {
    try {
      setLoading(true);
      const anneeScolaireId = localStorage.getItem('annee_scolaire_id') || '1';
      const response = await api.parametresPointage.getByAnnee(anneeScolaireId);
      
      if (response.success) {
        const data = response.data;

        // MySQL rend une colonne JSON tantot decodee, tantot en chaine selon le
        // pilote et la version. Et tant que la colonne n'existe pas, le champ
        // est absent : l'ecran doit alors partir d'un objet vide, pas de
        // `undefined` — sans quoi la premiere saisie planterait.
        if (typeof data.jours_speciaux === 'string') {
          try {
            data.jours_speciaux = JSON.parse(data.jours_speciaux);
          } catch {
            data.jours_speciaux = {};
          }
        }
        if (!data.jours_speciaux || typeof data.jours_speciaux !== 'object') {
          data.jours_speciaux = {};
        }

        // La route double la colonne DATE sous un alias formate en SQL. La
        // colonne brute arrive en objet Date, que le passage en JSON decale
        // d'un jour selon le fuseau — l'ecran afficherait la veille. On ne
        // garde donc que l'alias, et '' quand aucune date n'est posee.
        data.pause_deduction_date_effet = data.pause_deduction_date_effet_iso || '';

        
        setParametres(data);
        setInitialParametres(JSON.parse(JSON.stringify(data)));
      }
    } catch (error) {
      console.error('❌ Erreur chargement paramètres:', error);
      showToast('error', 'Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

 const loadGroupes = async () => {
  try {
    const response = await api.groupesHoraires.getAll();
    if (response.success) {
      // Parser les JSON pour chaque groupe
      const groupesParsed = response.data.map(g => ({
        ...g,
        jours_speciaux: g.jours_speciaux ? 
          (typeof g.jours_speciaux === 'string' ? JSON.parse(g.jours_speciaux) : g.jours_speciaux)
          : null
      }));
      
      setGroupes(groupesParsed);
      if (groupesParsed.length > 0 && !groupeSelectionne) {
        setGroupeSelectionne(groupesParsed[0]);
        loadEmployesGroupe(groupesParsed[0].id);
      }
    }
  } catch (error) {
    console.error('❌ Erreur chargement groupes:', error);
  }
};

  const loadEmployesNonAffectes = async () => {
    try {
      const response = await api.groupesHoraires.getEmployesNonAffectes();
      if (response.success) {
        setEmployesNonAffectes(response.data);
      }
    } catch (error) {
      console.error('❌ Erreur chargement employés non affectés:', error);
    }
  };

  const loadEmployesGroupe = async (groupeId) => {
    try {
      const response = await api.groupesHoraires.getEmployes(groupeId);
      if (response.success) {
        setEmployesGroupe(response.data);
        setEmployesSelectionnes(response.data.map(e => e.id));
      }
    } catch (error) {
      console.error('❌ Erreur chargement employés du groupe:', error);
    }
  };

  // ============================================
  // GESTION DES CHANGEMENTS (Onglets 1, 2, 3)
  // ============================================
  const handleChange = (field, value) => {
    setParametres(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const isParamEnabled = (field, fallback = false) => {
    const value = parametres[field];
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    return ['1', 'true', 'oui', 'yes'].includes(String(value).trim().toLowerCase());
  };

  /**
   * Un jour special de l'etablissement.
   *
   * Une heure videe retire la cle, et un jour sans aucune heure disparait : on
   * ne garde pas `{ vendredi: {} }`, qui n'exprimerait rien tout en ayant l'air
   * d'une regle posee.
   */
  /**
   * L'heure obtenue en decalant `heure` de `minutes`.
   *
   * Sert a montrer sous chaque champ ce qu'il produit : « 15 min avant » ne dit
   * rien, « 15 min avant, soit 07:15 » se verifie d'un coup d'oeil.
   */
  const decaler = (heure, minutes) => {
    if (!heure) return '—';
    const [h, m] = heure.substring(0, 5).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '—';
    const total = Math.max(0, Math.min(23 * 60 + 59, h * 60 + m + Number(minutes || 0)));
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  /** '01:30:00' -> 90. Une valeur illisible rend 0 plutot que NaN. */
  const tempsEnMinutes = (temps) => {
    if (!temps) return 0;
    const [h, m] = String(temps).substring(0, 5).split(':').map(Number);
    return Number.isNaN(h) || Number.isNaN(m) ? 0 : h * 60 + m;
  };

  /** 90 -> '01:30:00'. Borne a 12 h : au-dela ce n'est plus une pause. */
  const minutesEnTemps = (minutes) => {
    const t = Math.max(0, Math.min(12 * 60, Number(minutes) || 0));
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}:00`;
  };

  const handleJourSpecial = (jour, champ, valeur) => {
    setParametres(prev => {
      const tous = { ...(prev.jours_speciaux || {}) };
      const duJour = { ...(tous[jour] || {}) };
      if (valeur) duJour[champ] = valeur + ':00';
      else delete duJour[champ];
      if (Object.keys(duJour).length) tous[jour] = duJour;
      else delete tous[jour];
      return { ...prev, jours_speciaux: tous };
    });
    setHasChanges(true);
  };

  /**
   * Les champs numeriques d'un jour special — aujourd'hui la duree de pause.
   *
   * `handleJourSpecial` ne convient pas : il ajoute ':00' et surtout il efface
   * la valeur quand elle est fausse. Or zero est ici la valeur la plus utile
   * de toutes — c'est elle qui dit « pas de pause ce jour-la ». Seule la
   * chaine vide efface.
   */
  const handleJourSpecialNombre = (jour, champ, valeur) => {
    setParametres(prev => {
      const tous = { ...(prev.jours_speciaux || {}) };
      const duJour = { ...(tous[jour] || {}) };
      if (valeur === '' || valeur === null) delete duJour[champ];
      else duJour[champ] = Math.max(0, Math.min(720, parseInt(valeur, 10) || 0));
      if (Object.keys(duJour).length) tous[jour] = duJour;
      else delete tous[jour];
      return { ...prev, jours_speciaux: tous };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const anneeScolaireId = localStorage.getItem('annee_scolaire_id') || '1';
      
      const response = await api.parametresPointage.updateByAnnee(anneeScolaireId, parametres);
      
      if (response.success) {
        showToast('success', 'Paramètres enregistrés avec succès');
        setHasChanges(false);
        setInitialParametres(JSON.parse(JSON.stringify(parametres)));
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      showToast('error', 'Erreur lors de la sauvegarde des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialParametres) {
      setParametres(JSON.parse(JSON.stringify(initialParametres)));
      setHasChanges(false);
    }
  };

  // ============================================
  // GESTION GROUPES HORAIRES (Onglet 4)
  // ============================================
  const handleSelectionnerGroupe = async (groupe) => {
    setGroupeSelectionne(groupe);
    await loadEmployesGroupe(groupe.id);
  };

  const handleCreerGroupe = async () => {
    try {
      setSaving(true);
      const response = await api.groupesHoraires.create(nouveauGroupe);
      if (response.success) {
        setShowModalGroupe(false);
        await loadGroupes();
        showToast('success', 'Groupe créé avec succès');
        setNouveauGroupe({
          nom: '',
          description: '',
          heure_debut: '08:00:00',
          heure_fin: '17:00:00',
          tolerance_arrivee_minutes: 20,
          tolerance_depart_minutes: 10,
          tolerance_debut_avant_minutes: 15,
          tolerance_debut_apres_minutes: 20,
          tolerance_fin_avant_minutes: 10,
          tolerance_fin_apres_minutes: 10,
          couleur: '#3084b5',
          jours_travailles: ['lundi','mardi','mercredi','jeudi','vendredi']
        });
      }
    } catch (error) {
      console.error('❌ Erreur création groupe:', error);
      showToast('error', 'Erreur lors de la création du groupe');
    } finally {
      setSaving(false);
    }
  };

 const handleModifierGroupe = async () => {
  try {
    setSaving(true);
    
    // Préparer les données avec jours_speciaux en JSON
    const dataToSend = {
      ...groupeSelectionne,
      jours_speciaux: groupeSelectionne.jours_speciaux 
        ? (typeof groupeSelectionne.jours_speciaux === 'string' 
            ? groupeSelectionne.jours_speciaux 
            : JSON.stringify(groupeSelectionne.jours_speciaux))
        : null
    };
    
    const response = await api.groupesHoraires.update(groupeSelectionne.id, dataToSend);
    if (response.success) {
      await loadGroupes();
      showToast('success', 'Groupe modifié avec succès');
    }
  } catch (error) {
    console.error('❌ Erreur modification groupe:', error);
    showToast('error', 'Erreur lors de la modification du groupe');
  } finally {
    setSaving(false);
  }
};

  const handleSupprimerGroupe = async (groupeId) => {
  
  try {
    const response = await api.groupesHoraires.delete(groupeId);
    if (response.success) {
      await loadGroupes();
      setGroupeSelectionne(null);
      showToast('success', 'Groupe supprimé avec succès');
    } else {
      // Afficher le message d'erreur du backend
      showToast('error', response.message || 'Erreur lors de la suppression du groupe');
    }
  } catch (error) {
    console.error('❌ Erreur suppression groupe:', error);
    showToast('error', error.message || 'Erreur lors de la suppression du groupe');
  }
};

  const handleAffecterEmployes = async () => {
    try {
      setSaving(true);
      const response = await api.groupesHoraires.affecterEmployes(
        groupeSelectionne.id,
        employesSelectionnes
      );
      if (response.success) {
        await loadGroupes();
        await loadEmployesGroupe(groupeSelectionne.id);
        await loadEmployesNonAffectes();
        showToast('success', `${employesSelectionnes.length} employé(s) affecté(s) avec succès`);
      }
    } catch (error) {
      console.error('❌ Erreur affectation employés:', error);
      showToast('error', 'Erreur lors de l\'affectation des employés');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEmploye = (employeId) => {
    setEmployesSelectionnes(prev => {
      if (prev.includes(employeId)) {
        return prev.filter(id => id !== employeId);
      } else {
        return [...prev, employeId];
      }
    });
  };

  const handleFixedToleranceChange = (field, value) => {
    setParametres(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'fixe_tolerance_debut_apres_minutes') next.arrivee_tolerance_minutes = value;
      if (field === 'fixe_tolerance_fin_avant_minutes') next.depart_tolerance_minutes = value;
      return next;
    });
    setHasChanges(true);
  };

  const handleChangeGroupe = (field, value) => {
    setGroupeSelectionne(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'tolerance_debut_apres_minutes') next.tolerance_arrivee_minutes = value;
      if (field === 'tolerance_fin_avant_minutes') next.tolerance_depart_minutes = value;
      return next;
    });
  };

  const handleNouveauGroupeToleranceChange = (field, value) => {
    setNouveauGroupe(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'tolerance_debut_apres_minutes') next.tolerance_arrivee_minutes = value;
      if (field === 'tolerance_fin_avant_minutes') next.tolerance_depart_minutes = value;
      return next;
    });
  };

  const handleJourToggleGroupe = (jour) => {
    setGroupeSelectionne(prev => {
      const jours = [...(prev.jours_travailles || [])];
      const index = jours.indexOf(jour);
      if (index > -1) {
        jours.splice(index, 1);
      } else {
        jours.push(jour);
      }
      return { ...prev, jours_travailles: jours };
    });
  };



  // ============================================
// GESTION JOURS SPÉCIAUX
// ============================================
const handleAjouterJourSpecial = () => {
  // Une exception peut ne porter que sur la pause — « le mercredi, ce groupe
  // ne fait pas de pause » — sans toucher a l'heure de fin.
  const poseQuelqueChose = Boolean(jourSpecialTemp.heure_fin)
    || Boolean(jourSpecialTemp.pause_debut)
    || jourSpecialTemp.pause_duree_minutes !== '';
  if (!groupeSelectionne || !jourSpecialTemp.jour || !poseQuelqueChose) return;
  
  const joursSpeciaux = groupeSelectionne.jours_speciaux || {};
  
  // Vérifier si le jour existe déjà
  if (joursSpeciaux[jourSpecialTemp.jour]) {
    showToast('error', `${jourSpecialTemp.jour} est déjà configuré comme jour spécial`);
    return;
  }
  
  // La duree n'est posee que si elle a ete saisie — et zero se pose, lui :
  // c'est la valeur qui dit « pas de pause ce jour-la ».
  const regle = {};
  if (jourSpecialTemp.heure_fin) {
    regle.heure_fin = jourSpecialTemp.heure_fin + ':00';
  }
  if (jourSpecialTemp.pause_debut) {
    regle.pause_debut = jourSpecialTemp.pause_debut + ':00';
  }
  if (jourSpecialTemp.pause_duree_minutes !== '') {
    regle.pause_duree_minutes = Math.max(0, Math.min(720, parseInt(jourSpecialTemp.pause_duree_minutes, 10) || 0));
  }
  joursSpeciaux[jourSpecialTemp.jour] = regle;
  
  setGroupeSelectionne(prev => ({
    ...prev,
    jours_speciaux: joursSpeciaux
  }));
  
  showToast('success', `Jour spécial ${jourSpecialTemp.jour} ajouté`);
  
  // Reset le formulaire
  setJourSpecialTemp({
    jour: '',
    heure_fin: '',
    pause_debut: '',
    pause_duree_minutes: ''
  });
};

const handleSupprimerJourSpecial = (jour) => {
  if (!groupeSelectionne) return;
  
  const joursSpeciaux = { ...(groupeSelectionne.jours_speciaux || {}) };
  delete joursSpeciaux[jour];
  
  setGroupeSelectionne(prev => ({
    ...prev,
    jours_speciaux: Object.keys(joursSpeciaux).length > 0 ? joursSpeciaux : null
  }));
  
  showToast('success', `Jour spécial ${jour} supprimé`);
};


  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="w-full max-w-full mx-auto px-6">
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          icone={SettingsIcon}
          titre="Paramétrage des pointages"
          sousTitre="Horaires fixes et variables, groupes horaires, badges RFID et rapports"
        />
        <ChargementSection accent="#3084b5" libelle="Chargement des paramètres…" />
      </div>
    );
  }

  // ============================================
  // RENDER PRINCIPAL
  // ============================================
  return (
    <div className="w-full max-w-full mx-auto px-6">
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={SettingsIcon}
        titre="Paramétrage des pointages"
        sousTitre="Horaires fixes et variables, groupes horaires, badges RFID et rapports"
      />

      {/* Cet encadre etait en pied de page, sous quatre onglets charges de
          champs : on ne le rencontrait qu'apres avoir tout regle. Ce qui
          conditionne une saisie se lit avant la saisie. */}
      {/* Encadre d'information — norme section 8. */}
      <div className="mb-6 rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
        <div className="flex gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
            <Info className="h-4 w-4 text-[#246990]" />
          </div>
            <div className="text-xs leading-relaxed text-gray-600">
              <p className="mb-1 text-[13px] font-semibold text-[#246990]">Ce qu'il faut savoir</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>• Les paramètres sont liés à l'année scolaire en cours</li>
                <li>• Les modifications affectent uniquement les futurs pointages</li>
                <li>• Le système de détection intelligente s'adapte automatiquement aux tolérances configurées</li>
                {activeTab === 'gestion-horaires' && (
                  <li>• Les groupes d'horaires permettent de gérer facilement des équipes avec des horaires différents</li>
                )}
              </ul>
            </div>
          </div>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* grid-cols-4 depuis l'ajout de l'onglet des badges. La page entière
            est déjà filtrée sur la licence `cap.rfid` en amont
            (ParametrageApp.jsx, LICENCE_PAR_SECTION['pointages']) : on
            n'arrive ici que si l'école a les badges, et le quatrième onglet n'a
            donc pas à se compter conditionnellement. */}
        {/* grid-cols-5 depuis l'onglet « Rapports » (14/10/2026). La norme
            (PARAMETRAGE-MODULES.md §2) demande de scinder une page au-delà de
            quatre onglets ; écart assumé : les rapports ne règlent rien, ils
            montrent l'effet des réglages des trois premiers onglets, et c'est
            ici que Daniel les a demandés. */}
        <TabsList className="grid grid-cols-5 mb-6 bg-[#246990]/8 p-1 rounded-xl">
          <TabsTrigger
            value="horaires-fixes"
            className="data-[state=active]:bg-[#3084b5] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Users size={15} className="mr-2" />
            <span>Horaires fixes</span>
          </TabsTrigger>
          <TabsTrigger
            value="horaires-variables"
            className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <GraduationCap size={15} className="mr-2" />
            <span>Horaires variables</span>
          </TabsTrigger>
          <TabsTrigger
            value="gestion-horaires"
            className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <ClipboardList size={15} className="mr-2" />
            <span>Groupes horaires</span>
          </TabsTrigger>
          <TabsTrigger
            value="badges-rfid"
            className="data-[state=active]:bg-[#ae7467] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <IdCard size={15} className="mr-2" />
            <span>Badges RFID</span>
          </TabsTrigger>
          <TabsTrigger
            value="rapports"
            className="data-[state=active]:bg-[#7c3aed] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <FileText size={15} className="mr-2" />
            <span>Rapports</span>
          </TabsTrigger>
        </TabsList>

        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          
          {/* ========================================== */}
          {/* ONGLET 1 : HORAIRES FIXES */}
          {/* ========================================== */}
          <TabsContent value="horaires-fixes" className="mt-0 focus-visible:outline-none">
            <div className="space-y-8">
              {/* Header Onglet */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#3084b5] to-[#246990] rounded-xl flex items-center justify-center">
                    <Users className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Personnel à Horaires Fixes</h2>
                    <p className="text-sm text-gray-600">Configuration pour le personnel FIXE et CDI</p>
                  </div>
                </div>
                <BoutonsEnregistrer
                  accent="#3084b5"
                  hasChanges={hasChanges}
                  saving={saving}
                  onReset={handleCancel}
                  onSave={handleSave}
                />
              </div>

              {/* Plage d'arrivée */}
              <CarteReglages titre="Arrivée" accent="#3084b5">
                <p className="mb-4 text-xs leading-relaxed text-gray-500">
                  Ces réglages ne concernent que les employés <strong className="text-gray-700">sans
                  groupe horaire</strong> : ceux qui en ont un suivent l'heure de leur groupe.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure d'arrivée attendue
                    </label>
                    <input
                      type="time"
                      value={parametres.arrivee_heure_prevue?.substring(0, 5) || ''}
                      onChange={(e) => handleChange('arrivee_heure_prevue', e.target.value + ':00')}
                      className="w-full px-4 py-2 border border-[#3084b5]/40 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      L'heure à laquelle l'employé doit être là
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marge d'ouverture (min avant)
                    </label>
                    <input
                      type="number"
                      value={parametres.fixe_tolerance_debut_avant_minutes || 0}
                      onChange={(e) => handleFixedToleranceChange('fixe_tolerance_debut_avant_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-semibold text-gray-700">
                        {parametres.arrivee_heure_prevue?.substring(0, 5) || '—'}
                      </span>
                      {' '}− <span className="font-semibold text-gray-700">
                        {parametres.fixe_tolerance_debut_avant_minutes || 0} min
                      </span>
                      {' '}= <span className="font-semibold text-gray-700">
                        {decaler(parametres.arrivee_heure_prevue, -(parametres.fixe_tolerance_debut_avant_minutes || 0))}
                      </span>
                      {' '}: la badgeuse ouvre à cette heure
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Badgeage refusé après
                    </label>
                    <input
                      type="time"
                      value={parametres.arrivee_fin?.substring(0, 5) || ''}
                      onChange={(e) => handleChange('arrivee_fin', e.target.value + ':00')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Passé cette heure, plus aucune arrivée n'est acceptée
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marge après l'heure attendue (min)
                    </label>
                    <input
                      type="number"
                      value={parametres.fixe_tolerance_debut_apres_minutes || parametres.arrivee_tolerance_minutes || 0}
                      onChange={(e) => handleFixedToleranceChange('fixe_tolerance_debut_apres_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-semibold text-gray-700">
                        {parametres.arrivee_heure_prevue?.substring(0, 5) || '—'}
                      </span>
                      {' '}+ <span className="font-semibold text-gray-700">
                        {parametres.fixe_tolerance_debut_apres_minutes || parametres.arrivee_tolerance_minutes || 0} min
                      </span>
                      {' '}= <span className="font-semibold text-gray-700">
                        {decaler(parametres.arrivee_heure_prevue, parametres.fixe_tolerance_debut_apres_minutes || parametres.arrivee_tolerance_minutes || 0)}
                      </span>
                      {' '}: arriver après est compté comme un retard — badger reste possible
                    </p>
                  </div>
                </div>

                {/* Un piege de configuration : refuser le badgeage avant meme
                    que le retard ne commence. L'employe en retard ne peut alors
                    plus pointer du tout, et ressort absent au lieu d'en retard. */}
                {(() => {
                  const retardA = decaler(
                    parametres.arrivee_heure_prevue,
                    parametres.fixe_tolerance_debut_apres_minutes || parametres.arrivee_tolerance_minutes || 0
                  );
                  const ferme = parametres.arrivee_fin?.substring(0, 5);
                  if (!ferme || retardA === '—' || ferme > retardA) return null;
                  return (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                      <p className="text-xs leading-relaxed text-amber-800">
                        La badgeuse ferme à <strong>{ferme}</strong>, avant même que le retard
                        ne commence (<strong>{retardA}</strong>). Un employé arrivant après{' '}
                        {ferme} ne pourra pas pointer du tout et ressortira <strong>absent</strong>,
                        pas en retard.
                      </p>
                    </div>
                  );
                })()}

                {/* « Fenêtre — début » a disparu : elle portait `arrivee_debut`,
                    que le code n'atteint jamais. L'ouverture de la badgeuse se
                    calcule depuis l'heure attendue et la tolérance — deux champs
                    pour une seule idée, dont le visible ne servait pas. */}
              </CarteReglages>

              <CarteReglages titre="Départ" accent="#c66d59">
                <p className="mb-3 text-xs leading-relaxed text-gray-500">
                  Deux seuils <strong className="text-gray-700">emboîtés</strong>, et non deux
                  règles concurrentes : le premier dit à partir de quand la badgeuse accepte
                  une sortie, le second à partir de quand cette sortie est jugée normale.
                  Entre les deux, le pointage est <em>enregistré</em> et <em>signalé</em>.
                  {' '}Seuls les employés <strong className="text-gray-700">sans groupe
                  horaire</strong> sont concernés.
                </p>

                {/* Les zones de la journee, construites depuis les seuils reels.
                    Une zone vide n'est pas affichee : « 16:50 -> 16:50 » se lisait
                    comme une plage alors qu'elle ne contient aucun instant, et
                    contredisait la ligne du dessus. Ce qui n'existe pas ne
                    s'affiche pas. */}
                {(() => {
                  const plancher = parametres.depart_debut?.substring(0, 5) || null;
                  const tropTot = decaler(
                    parametres.depart_heure_prevue,
                    -(parametres.fixe_tolerance_fin_avant_minutes || parametres.depart_tolerance_minutes || 0)
                  );
                  const sup = decaler(
                    parametres.depart_heure_prevue,
                    parametres.fixe_tolerance_fin_apres_minutes || 0
                  );
                  const ferme = parametres.depart_fin?.substring(0, 5) || null;

                  // Le debut reel de chaque zone : le plancher l'emporte quand il
                  // est plus tardif, puisqu'avant lui rien n'est enregistre.
                  const apres = (a, b) => (a && b ? (a > b ? a : b) : a || b);
                  const debutTropTot = apres(plancher, null);
                  const debutATemps = apres(plancher, tropTot);
                  const debutSup = apres(plancher, sup);

                  // La duree de chaque plage, affichee a droite.
                  // « 10 min de marge » donne une zone de 5 minutes des que le
                  // plancher la rogne : montrer la duree evite d'avoir a faire
                  // la soustraction de tete.
                  const enMin = (h) => {
                    if (!h || h === '—') return null;
                    const [a, b] = h.split(':').map(Number);
                    return Number.isNaN(a) ? null : a * 60 + b;
                  };
                  const duree = (a, b) => {
                    const d = enMin(a);
                    const f = enMin(b);
                    if (d === null || f === null || f <= d) return null;
                    const m = f - d;
                    return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`;
                  };

                  const zones = [
                    plancher && {
                      quand: `avant ${plancher}`,
                      quoi: "Sortie refusée — rien n'est enregistré",
                      ton: 'bg-red-50 text-red-800',
                      vide: false
                    },
                    {
                      quand: `${debutTropTot || '—'} → ${tropTot}`,
                      duree: duree(debutTropTot, tropTot),
                      quoi: 'Sortie acceptée, signalée « parti trop tôt »',
                      ton: 'bg-amber-50 text-amber-800',
                      vide: Boolean(debutTropTot && tropTot && debutTropTot >= tropTot)
                    },
                    {
                      quand: `${debutATemps || tropTot} → ${sup}`,
                      duree: duree((debutATemps || tropTot), sup),
                      quoi: "À l'heure",
                      ton: 'bg-teal-50 text-teal-800',
                      vide: Boolean(debutATemps && sup && debutATemps >= sup)
                    },
                    {
                      quand: `${debutSup || sup} → ${ferme || '—'}`,
                      duree: duree((debutSup || sup), ferme),
                      quoi: 'Heures supplémentaires',
                      ton: 'bg-[#3084b5]/10 text-[#246990]',
                      vide: Boolean(ferme && debutSup && debutSup >= ferme)
                    }
                  ].filter((z) => z && !z.vide);

                  return (
                    <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 text-xs">
                      {zones.map((zone, i) => (
                        <div
                          key={i}
                          className={`flex flex-wrap items-center gap-3 px-4 py-2 ${zone.ton} ${i > 0 ? 'border-t border-white/60' : ''}`}
                        >
                          <span className="w-40 font-mono font-semibold">{zone.quand}</span>
                          <span className="flex-1">{zone.quoi}</span>
                          {zone.duree && (
                            <span className="font-mono text-[11px] opacity-70">{zone.duree}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure de départ attendue
                    </label>
                    <input
                      type="time"
                      value={parametres.depart_heure_prevue?.substring(0, 5) || ''}
                      onChange={(e) => handleChange('depart_heure_prevue', e.target.value + ':00')}
                      className="w-full px-4 py-2 border border-[#c66d59]/40 rounded-lg focus:ring-2 focus:ring-[#c66d59] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      L'heure jusqu'à laquelle l'employé doit rester
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sortie refusée avant
                    </label>
                    <input
                      type="time"
                      value={parametres.depart_debut?.substring(0, 5) || ''}
                      onChange={(e) => handleChange('depart_debut', e.target.value + ':00')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c66d59] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Rien n'est enregistré : l'employé devra rebadger plus tard
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Badgeage refusé après
                    </label>
                    <input
                      type="time"
                      value={parametres.depart_fin?.substring(0, 5) || ''}
                      onChange={(e) => handleChange('depart_fin', e.target.value + ':00')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c66d59] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Fin de journée au-delà de laquelle plus rien n'est accepté
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marge avant l'heure attendue (min)
                    </label>
                    <input
                      type="number"
                      value={parametres.fixe_tolerance_fin_avant_minutes || parametres.depart_tolerance_minutes || 0}
                      onChange={(e) => handleFixedToleranceChange('fixe_tolerance_fin_avant_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c66d59] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                    {(() => {
                      const marge = parametres.fixe_tolerance_fin_avant_minutes || parametres.depart_tolerance_minutes || 0;
                      const attendue = parametres.depart_heure_prevue?.substring(0, 5) || '—';
                      const tropTot = decaler(parametres.depart_heure_prevue, -marge);
                      const plancher = parametres.depart_debut?.substring(0, 5);

                      // La valeur au-dela de laquelle le reglage n'a plus d'effet :
                      // l'ecart entre l'heure attendue et le plancher. C'est la
                      // seule facon de repondre a « combien dois-je mettre ? »
                      // sans faire le calcul de tete.
                      const enMinutes = (h) => {
                        if (!h || h === '—') return null;
                        const [a, b] = h.split(':').map(Number);
                        return Number.isNaN(a) ? null : a * 60 + b;
                      };
                      const margeMax = enMinutes(attendue) !== null && enMinutes(plancher) !== null
                        ? enMinutes(attendue) - enMinutes(plancher)
                        : null;
                      const inatteignable = plancher && tropTot !== '—' && plancher >= tropTot;

                      return (
                        <p className={`text-xs mt-1 ${inatteignable ? 'text-amber-700' : 'text-gray-500'}`}>
                          {/* L'arithmetique, en toutes lettres : c'est elle qui
                              manquait. « 10 min » donnait une zone de 5 minutes,
                              et rien ne disait pourquoi. */}
                          <span className="font-semibold text-gray-700">{attendue}</span>
                          {' '}− <span className="font-semibold text-gray-700">{marge} min</span>
                          {' '}= <span className="font-semibold text-gray-700">{tropTot}</span>
                          {inatteignable ? (
                            <>
                              {' '}— or la sortie est déjà refusée avant{' '}
                              <span className="font-semibold">{plancher}</span> : ce réglage
                              ne change donc rien.
                              {margeMax !== null && margeMax > 0 && (
                                <> Mettez moins de <span className="font-semibold">{margeMax} min</span>.</>
                              )}
                            </>
                          ) : (
                            <>
                              {' '}: partir avant cette heure est compté comme départ anticipé.
                            </>
                          )}
                        </p>
                      );
                    })()}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heures sup. après (min)
                    </label>
                    <input
                      type="number"
                      value={parametres.fixe_tolerance_fin_apres_minutes || 0}
                      onChange={(e) => handleFixedToleranceChange('fixe_tolerance_fin_apres_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c66d59] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Comptées à partir de{' '}
                      <span className="font-semibold text-gray-700">
                        {decaler(parametres.depart_heure_prevue, parametres.fixe_tolerance_fin_apres_minutes || 0)}
                      </span>
                      {' '}si les heures supplémentaires sont actives
                    </p>
                  </div>
                </div>

                {/* Le piege symetrique de celui de l'arrivee : « Sortie refusee
                    avant » posee apres la ligne du « trop tot » rend celle-ci
                    inatteignable.
                    Aucun depart anticipe ne serait jamais enregistre — il serait
                    simplement refuse, et l'employe ressortirait sans heure de
                    sortie du tout. */}
                {(() => {
                  const plancher = parametres.depart_debut?.substring(0, 5);
                  const tropTot = decaler(
                    parametres.depart_heure_prevue,
                    -(parametres.fixe_tolerance_fin_avant_minutes || parametres.depart_tolerance_minutes || 0)
                  );
                  // `<` et non `<=` : a egalite la zone du depart anticipe
                  // est vide, et la regle est deja inatteignable.
                  if (!plancher || tropTot === '—' || plancher < tropTot) return null;
                  return (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                      <p className="text-xs leading-relaxed text-amber-800">
                        La sortie est refusée avant <strong>{plancher}</strong>, et le départ
                        anticipé ne commence qu'à <strong>{tropTot}</strong> : il ne reste
                        aucun moment où un départ anticipé pourrait être enregistré. Un employé
                        qui part plus tôt est simplement refusé, et sa journée reste{' '}
                        <strong>sans heure de sortie</strong>.
                        {' '}Avancez « Sortie refusée avant », ou augmentez la tolérance.
                      </p>
                    </div>
                  );
                })()}
              </CarteReglages>

              {/* Gestion des pauses */}
              <CarteReglages titre="Gestion des pauses" accent="#319190">
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parametres.mode_pause === 'pointage'}
                      onChange={(e) => handleChange('mode_pause', e.target.checked ? 'pointage' : 'automatique')}
                      className="w-5 h-5 text-[#319190] border-gray-300 rounded focus:ring-[#319190]"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Faire badger les pauses
                    </span>
                  </label>
                  <p className="mt-1.5 ml-8 text-xs leading-relaxed text-gray-500">
                    {parametres.mode_pause === 'pointage'
                      ? "Quatre badges par jour : arrivée, sortie de pause, retour de pause, départ. La durée réelle de la pause est retirée du temps travaillé."
                      : "Deux badges par jour. Une durée fixe est retirée du temps travaillé, sans que personne ne badge sa pause."}
                  </p>
                </div>

                {/* Mode automatique : la duree deduite.
                    Elle valait une heure en base et aucun champ ne permettait
                    d'en changer. */}
                {parametres.mode_pause !== 'pointage' && (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="max-w-xl">
                        <p className="text-sm font-semibold text-gray-800">
                          Durée déduite automatiquement
                        </p>
                        <p className="mt-1 text-[11px] leading-relaxed text-gray-400">
                          Retirée du temps travaillé de chaque journée complète.
                        </p>
                      </div>
                      <div className="relative w-32 flex-shrink-0">
                        <input
                          type="number"
                          min="0"
                          max="720"
                          value={tempsEnMinutes(parametres.pause_duree_auto)}
                          onChange={(e) => handleChange('pause_duree_auto', minutesEnTemps(e.target.value))}
                          className="h-9 w-full rounded-lg border border-gray-300 pr-12 text-center focus:border-transparent focus:ring-2 focus:ring-[#319190]"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                          min
                        </span>
                      </div>
                    </div>

                    {/* La date d'effet. Sans elle, activer la deduction
                        amputerait d'un coup toutes les journees a venir d'un
                        mois de paie deja entame. */}
                    <div className="mt-4 flex flex-wrap items-start justify-between gap-4 border-t border-gray-100 pt-4">
                      <div className="max-w-xl">
                        <p className="text-sm font-semibold text-gray-800">
                          Appliquée à partir du
                        </p>
                        <p className="mt-1 text-[11px] leading-relaxed text-gray-400">
                          {parametres.pause_deduction_date_effet
                            ? <>Les journées avant cette date gardent leur durée. Choisissez le
                                premier jour d'un mois de paie pour ne pas le couper en deux.</>
                            : <>Tant qu'aucune date n'est posée, <strong className="text-gray-500">rien
                                n'est déduit</strong> : les durées restent celles d'aujourd'hui.</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <input
                          type="date"
                          value={parametres.pause_deduction_date_effet || ''}
                          onChange={(e) => handleChange('pause_deduction_date_effet', e.target.value)}
                          className="h-9 rounded-lg border border-gray-300 px-3 focus:border-transparent focus:ring-2 focus:ring-[#319190]"
                        />
                        {parametres.pause_deduction_date_effet && (
                          <button
                            type="button"
                            onClick={() => handleChange('pause_deduction_date_effet', '')}
                            className="text-xs text-[#3084b5] hover:underline"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>

                    {parametres.pause_deduction_date_effet && (
                      <p className="mt-3 text-xs leading-relaxed text-gray-500">
                        À partir du{' '}
                        <span className="font-semibold text-gray-700">
                          {parametres.pause_deduction_date_effet.split('-').reverse().join('/')}
                        </span>
                        , chaque journée complète comptera{' '}
                        <span className="font-semibold text-gray-700">
                          {tempsEnMinutes(parametres.pause_duree_auto)} min
                        </span>
                        {' '}de moins. Personne ne badge sa pause : la durée est retirée
                        sans qu'on sache quand elle a été prise.
                      </p>
                    )}
                  </div>
                )}

                {/* Mode pointage : une heure d'ancrage et deux marges, comme
                    l'arrivee et le depart. */}
                {parametres.mode_pause === 'pointage' && (() => {
                  const debut = parametres.pause_debut_prevue?.substring(0, 5) || '';
                  const duree = parametres.pause_duree_prevue_minutes ?? 60;
                  const marge = parametres.pause_tolerance_minutes ?? 10;
                  const fin = decaler(parametres.pause_debut_prevue, duree);
                  const ouverture = decaler(parametres.pause_debut_prevue, -marge);
                  const limite = decaler(parametres.pause_debut_prevue, duree + marge);

                  const enMin = (h) => {
                    if (!h || h === '—') return null;
                    const [a, b] = h.split(':').map(Number);
                    return Number.isNaN(a) ? null : a * 60 + b;
                  };
                  const ecart = (a, b) => {
                    const x = enMin(a);
                    const y = enMin(b);
                    if (x === null || y === null || y <= x) return null;
                    const m = y - x;
                    return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`;
                  };

                  const zones = [
                    { quand: `avant ${ouverture}`, quoi: "Badgeage refusé", ton: 'bg-red-50 text-red-800', duree: null },
                    { quand: `${ouverture} → ${debut}`, quoi: 'Sortie acceptée, signalée « pause anticipée »', ton: 'bg-amber-50 text-amber-800', duree: ecart(ouverture, debut) },
                    { quand: `${debut} → ${fin}`, quoi: 'En pause', ton: 'bg-teal-50 text-teal-800', duree: ecart(debut, fin) },
                    { quand: `${fin} → ${limite}`, quoi: 'Retour dans la marge', ton: 'bg-[#3084b5]/10 text-[#246990]', duree: ecart(fin, limite) },
                    { quand: `après ${limite}`, quoi: 'Retour signalé « tardif »', ton: 'bg-amber-50 text-amber-800', duree: null }
                  ].filter((z) => !z.quand.includes('→') || z.duree);

                  return (
                    <>
                      <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 text-xs">
                        {zones.map((zone, i) => (
                          <div
                            key={i}
                            className={`flex flex-wrap items-center gap-3 px-4 py-2 ${zone.ton} ${i > 0 ? 'border-t border-white/60' : ''}`}
                          >
                            <span className="w-40 font-mono font-semibold">{zone.quand}</span>
                            <span className="flex-1">{zone.quoi}</span>
                            {zone.duree && (
                              <span className="font-mono text-[11px] opacity-70">{zone.duree}</span>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Heure de début de pause
                          </label>
                          <input
                            type="time"
                            value={debut}
                            onChange={(e) => handleChange('pause_debut_prevue', e.target.value + ':00')}
                            className="w-full px-4 py-2 border border-[#319190]/40 rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Partir avant est signalé « pause anticipée »
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Durée de la pause (min)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="720"
                            value={duree}
                            onChange={(e) => handleChange('pause_duree_prevue_minutes', parseInt(e.target.value, 10) || 0)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            <span className="font-semibold text-gray-700">{debut || '—'}</span>
                            {' '}+ <span className="font-semibold text-gray-700">{duree} min</span>
                            {' '}= <span className="font-semibold text-gray-700">{fin}</span>
                            {' '}: fin de pause attendue. Sert aussi de repli si le retour
                            n'est pas badgé.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Marge (min)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="180"
                            value={marge}
                            onChange={(e) => handleChange('pause_tolerance_minutes', parseInt(e.target.value, 10) || 0)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Des deux côtés : badgeage ouvert dès{' '}
                            <span className="font-semibold text-gray-700">{ouverture}</span>,
                            retour toléré jusqu'à{' '}
                            <span className="font-semibold text-gray-700">{limite}</span>
                          </p>
                        </div>
                      </div>

                      <p className="mt-4 text-xs leading-relaxed text-gray-500">
                        Ces heures valent pour les employés <strong className="text-gray-700">sans
                        groupe horaire</strong>. Un groupe peut avoir sa propre pause — utile
                        quand la cantine et l'entretien s'échelonnent.
                        {' '}Revenir en avance n'est pas signalé : c'est du temps travaillé en plus.
                      </p>

                      <p className="mt-2 text-xs leading-relaxed text-gray-500">
                        Un badge posé hors de ces heures n'est jamais perdu : avant{' '}
                        <span className="font-mono font-semibold text-gray-700">{ouverture}</span>{' '}
                        ou après{' '}
                        <span className="font-mono font-semibold text-gray-700">{fin}</span>,
                        il compte comme un départ. Et si le retour n'est pas badgé du tout,
                        c'est la durée ci-dessus qui est retirée — l'oubli ne rallonge pas
                        la journée.
                      </p>
                    </>
                  );
                })()}
              </CarteReglages>

              {/* « Jours travailles » est parti le 12 septembre 2026.
                  Le reglage global n'etait lu par aucun code metier : les
                  seules occurrences trouvees etaient des alias SQL
                  (COUNT(DISTINCT date_session) as jours_travailles) et la
                  colonne du groupe horaire, qui est une autre table.

                  Les jours travailles fonctionnent au niveau du GROUPE
                  (`groupes_horaires.jours_travailles`, lu par
                  GroupesHorairesController) : c'est la qu'ils se reglent. */}

              {/* Les jours speciaux de l'etablissement.
                  Ils existaient deja par groupe horaire, mais 63 employes sur
                  67 n'appartiennent a aucun groupe : declarer « vendredi, tout
                  le monde finit a 14h » n'atteignait presque personne.
                  Un groupe garde le dernier mot en declarant le sien. */}
              <CarteReglages
                titre="Jours spéciaux de l'établissement"
                accent="#3084b5"
                compteur={`${Object.keys(parametres.jours_speciaux || {}).length} jour(s) réglé(s)`}
              >
                <p className="mb-4 text-xs leading-relaxed text-gray-500">
                  Une heure posée ici remplace l'horaire habituel de tout le monde ce
                  jour-là, y compris pour les employés d'un groupe horaire. Un groupe qui
                  doit y échapper déclare son propre jour spécial.
                  {' '}Laissez vide les jours sans exception.
                </p>
                <p className="mb-4 text-xs leading-relaxed text-gray-500">
                  La pause suit la même règle : laissée vide, c'est la pause habituelle qui
                  s'applique. <strong className="text-gray-700">Une durée à 0 veut dire
                  « pas de pause ce jour-là »</strong> — rien n'est retiré du temps travaillé,
                  et deux badges suffisent au lieu de quatre.
                </p>
                <div className="space-y-2">
                  {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'].map((jour) => {
                    const regle = parametres.jours_speciaux?.[jour] || {};
                    const pose = Boolean(
                      regle.heure_debut || regle.heure_fin
                      || regle.pause_debut || regle.pause_duree_minutes !== undefined
                    );
                    return (
                      <div
                        key={jour}
                        className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-2.5 transition-colors ${
                          pose ? 'border-[#3084b5]/40 bg-[#3084b5]/5' : 'border-gray-200'
                        }`}
                      >
                        <span className={`w-24 text-sm capitalize ${pose ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
                          {jour}
                        </span>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Début</label>
                          <input
                            type="time"
                            value={regle.heure_debut?.substring(0, 5) || ''}
                            onChange={(e) => handleJourSpecial(jour, 'heure_debut', e.target.value)}
                            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-transparent focus:ring-2 focus:ring-[#3084b5]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Fin</label>
                          <input
                            type="time"
                            value={regle.heure_fin?.substring(0, 5) || ''}
                            onChange={(e) => handleJourSpecial(jour, 'heure_fin', e.target.value)}
                            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-transparent focus:ring-2 focus:ring-[#3084b5]"
                          />
                        </div>
                        <div className="flex items-center gap-2 border-l border-gray-200 pl-3">
                          <label className="text-xs text-gray-500">Pause à</label>
                          <input
                            type="time"
                            value={regle.pause_debut?.substring(0, 5) || ''}
                            onChange={(e) => handleJourSpecial(jour, 'pause_debut', e.target.value)}
                            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-transparent focus:ring-2 focus:ring-[#319190]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">pendant</label>
                          <input
                            type="number"
                            min="0"
                            max="720"
                            placeholder={String(parametres.pause_duree_prevue_minutes ?? 60)}
                            value={regle.pause_duree_minutes ?? ''}
                            onChange={(e) => handleJourSpecialNombre(jour, 'pause_duree_minutes', e.target.value)}
                            className="h-9 w-20 rounded-lg border border-gray-300 px-3 text-sm focus:border-transparent focus:ring-2 focus:ring-[#319190]"
                          />
                          <span className="text-xs text-gray-400">min</span>
                        </div>
                        {regle.pause_duree_minutes === 0 && (
                          <span className="rounded-full bg-[#319190]/10 px-2.5 py-1 text-[11px] font-medium text-[#246990]">
                            sans pause
                          </span>
                        )}
                        {pose && (
                          <button
                            type="button"
                            onClick={() => {
                              handleJourSpecial(jour, 'heure_debut', '');
                              handleJourSpecial(jour, 'heure_fin', '');
                              handleJourSpecial(jour, 'pause_debut', '');
                              handleJourSpecialNombre(jour, 'pause_duree_minutes', '');
                            }}
                            className="ml-auto text-xs text-gray-400 transition-colors hover:text-red-600"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CarteReglages>
            </div>
          </TabsContent>

          {/* ========================================== */}
          {/* ONGLET 2 : HORAIRES VARIABLES */}
          {/* ========================================== */}
          <TabsContent value="horaires-variables" className="mt-0 focus-visible:outline-none">
            <div className="space-y-8">
              {/* Header Onglet */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#319190] to-[#2a7a7a] rounded-xl flex items-center justify-center">
                    <GraduationCap className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Enseignants à Horaires Variables</h2>
                    <p className="text-sm text-gray-600">Configuration des tolérances pour les créneaux de cours</p>
                  </div>
                </div>
                <BoutonsEnregistrer
                  accent="#319190"
                  hasChanges={hasChanges}
                  saving={saving}
                  onReset={handleCancel}
                  onSave={handleSave}
                />
              </div>

              {/* Tolérances Début de cours */}
              <CarteReglages titre="Tolérance DÉBUT de cours" accent="#319190">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Avant le début (minutes)</label>
                    <input
                      type="number"
                      value={parametres.tolerance_debut_avant_minutes || 0}
                      onChange={(e) => handleChange('tolerance_debut_avant_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="0"
                      max="60"
                    />
                    <p className="text-xs text-gray-500 mt-1">L'enseignant peut pointer X minutes avant</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Après le début (minutes)</label>
                    <input
                      type="number"
                      value={parametres.tolerance_debut_apres_minutes || 0}
                      onChange={(e) => handleChange('tolerance_debut_apres_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      min="0"
                      max="60"
                    />
                    <p className="text-xs text-gray-500 mt-1">Au-delà → marqué comme "retard"</p>
                  </div>
                </div>
              </CarteReglages>

              {/* Tolérances Fin de cours */}
              <CarteReglages titre="Tolérance FIN de cours" accent="#319190">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Avant la fin (minutes)</label>
                    <input
                      type="number"
                      value={parametres.tolerance_fin_avant_minutes || 0}
                      onChange={(e) => handleChange('tolerance_fin_avant_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      max="60"
                    />
                    <p className="text-xs text-gray-500 mt-1">Peut partir X minutes avant → "trop tôt"</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Après la fin (minutes)</label>
                    <input
                      type="number"
                      value={parametres.tolerance_fin_apres_minutes || 0}
                      onChange={(e) => handleChange('tolerance_fin_apres_minutes', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      min="0"
                      max="60"
                    />
                    <p className="text-xs text-gray-500 mt-1">Au-delà → "heures supplémentaires"</p>
                  </div>
                </div>
              </CarteReglages>

              {/* Regles RH et comptabilite */}
              <CarteReglages titre="Règles RH et comptabilité" accent="#246990">

                <div className="rounded-lg border border-blue-100 bg-white p-4 mb-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Ces règles décident quelles anomalies doivent être justifiées par les RH avant l'envoi des pointages à la comptabilité.
                    Les tolérances ci-dessus restent la base du calcul des heures effectives.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={isParamEnabled('anomalies_bloquantes', true)}
                      onChange={(e) => handleChange('anomalies_bloquantes', e.target.checked)}
                      className="w-5 h-5 text-[#246990] border-gray-300 rounded focus:ring-[#246990] mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Bloquer l'envoi comptable en cas d'anomalie</span>
                      <p className="text-xs text-gray-500">Un pointage bloquant doit être justifié ou corrigé avant l'envoi à la comptabilité.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={isParamEnabled('retard_bloquant', true)}
                      onChange={(e) => handleChange('retard_bloquant', e.target.checked)}
                      className="w-5 h-5 text-[#246990] border-gray-300 rounded focus:ring-[#246990] mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Retard bloquant</span>
                      <p className="text-xs text-gray-500">Un début de cours après la tolérance est envoyé en justification RH.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={isParamEnabled('sortie_anticipee_bloquante', true)}
                      onChange={(e) => handleChange('sortie_anticipee_bloquante', e.target.checked)}
                      className="w-5 h-5 text-[#246990] border-gray-300 rounded focus:ring-[#246990] mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Sortie anticipée bloquante</span>
                      <p className="text-xs text-gray-500">Une fin de cours trop tôt doit être validée avant comptabilisation.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={isParamEnabled('debut_trop_tot_bloquant', false)}
                      onChange={(e) => handleChange('debut_trop_tot_bloquant', e.target.checked)}
                      className="w-5 h-5 text-[#246990] border-gray-300 rounded focus:ring-[#246990] mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Arrivée trop tôt bloquante</span>
                      <p className="text-xs text-gray-500">Par défaut, un pointage avant la plage autorisée est tracé mais ne bloque pas.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={isParamEnabled('heures_supplementaires_actives', true)}
                      onChange={(e) => handleChange('heures_supplementaires_actives', e.target.checked)}
                      className="w-5 h-5 text-[#246990] border-gray-300 rounded focus:ring-[#246990] mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Détecter les heures supplémentaires</span>
                      <p className="text-xs text-gray-500">Une fin après la tolérance est signalée. Elle n'est pas payée sans validation ultérieure.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={isParamEnabled('heures_supplementaires_bloquantes', true)}
                      onChange={(e) => handleChange('heures_supplementaires_bloquantes', e.target.checked)}
                      disabled={!isParamEnabled('heures_supplementaires_actives', true)}
                      className="w-5 h-5 text-[#246990] border-gray-300 rounded focus:ring-[#246990] mt-0.5 disabled:opacity-40"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Heures supplémentaires à valider</span>
                      <p className="text-xs text-gray-500">Si activé, une fin trop tardive bloque l'envoi tant que les RH n'ont pas justifié.</p>
                    </div>
                  </label>
                </div>
              </CarteReglages>
            </div>
          </TabsContent>

          {/* ========================================== */}
          {/* ONGLET 3 : PARAMÈTRES AVANCÉS */}
          {/* ========================================== */}

          {/* ========================================== */}
          {/* ONGLET 4 : GESTION DES HORAIRES */}
          {/* ========================================== */}
          <TabsContent value="gestion-horaires" className="mt-0 focus-visible:outline-none">
            <div className="space-y-6">
              {/* Header Onglet */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#246990] to-[#1a4d6e] rounded-xl flex items-center justify-center">
                    <ClipboardList className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Gestion des Horaires</h2>
                    <p className="text-sm text-gray-600">Créer des groupes et affecter les employés</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowModalGroupe(true)}
                  className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300"
                >
                  <Plus className="h-4 w-4" />
                  Nouveau groupe
                </Button>
              </div>

              {/* Layout 3 colonnes */}
              <div className="grid grid-cols-12 gap-6">
                
                {/* ========================================== */}
                {/* COLONNE 1 : GROUPES (3/12) */}
                {/* ========================================== */}
                <div className="col-span-12 lg:col-span-3">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-4">Groupes ({groupes.length})</h3>
                    
                    <div className="space-y-2">
                      {groupes.map(groupe => (
                        <div
                          key={groupe.id}
                          onClick={() => handleSelectionnerGroupe(groupe)}
                          className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                            groupeSelectionne?.id === groupe.id
                              ? 'border-[#3084b5] bg-white shadow-md'
                              : 'border-transparent bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: groupe.couleur }}
                              ></div>
                              <span className="font-semibold text-sm text-gray-900">
                                {groupe.nom}
                              </span>
                            </div>
                            {groupeSelectionne?.id === groupe.id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGroupeASupprimer(groupe);
                                }}
                                aria-label={`Supprimer le groupe ${groupe.nom}`}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-gray-600">
                            {groupe.heure_debut?.substring(0,5)} - {groupe.heure_fin?.substring(0,5)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            👥 {groupe.nb_employes} employé{groupe.nb_employes > 1 ? 's' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ========================================== */}
                {/* COLONNE 2 : CONFIGURATION (5/12) */}
                {/* ========================================== */}
                <div className="col-span-12 lg:col-span-5">
                  {groupeSelectionne ? (
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Edit size={18} />
                        Configuration : {groupeSelectionne.nom}
                      </h3>

                      <div className="space-y-4">
                        {/* Nom et Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nom du groupe</label>
                          <input
                            type="text"
                            value={groupeSelectionne.nom}
                            onChange={(e) => handleChangeGroupe('nom', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <textarea
                            value={groupeSelectionne.description || ''}
                            onChange={(e) => handleChangeGroupe('description', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                            rows="2"
                          />
                        </div>

                        {/* Horaires */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Heure début</label>
                            <input
                              type="time"
                              value={groupeSelectionne.heure_debut?.substring(0,5) || ''}
                              onChange={(e) => handleChangeGroupe('heure_debut', e.target.value + ':00')}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Heure fin</label>
                            <input
                              type="time"
                              value={groupeSelectionne.heure_fin?.substring(0,5) || ''}
                              onChange={(e) => handleChangeGroupe('heure_fin', e.target.value + ':00')}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Tolerances */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Debut avant heure prevue (min)</label>
                            <input
                              type="number"
                              value={groupeSelectionne.tolerance_debut_avant_minutes ?? 15}
                              onChange={(e) => handleChangeGroupe('tolerance_debut_avant_minutes', parseInt(e.target.value) || 0)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                              min="0"
                              max="180"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Retard tolere (min)</label>
                            <input
                              type="number"
                              value={groupeSelectionne.tolerance_debut_apres_minutes ?? groupeSelectionne.tolerance_arrivee_minutes ?? 20}
                              onChange={(e) => handleChangeGroupe('tolerance_debut_apres_minutes', parseInt(e.target.value) || 0)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                              min="0"
                              max="180"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Depart anticipe tolere (min)</label>
                            <input
                              type="number"
                              value={groupeSelectionne.tolerance_fin_avant_minutes ?? groupeSelectionne.tolerance_depart_minutes ?? 10}
                              onChange={(e) => handleChangeGroupe('tolerance_fin_avant_minutes', parseInt(e.target.value) || 0)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                              min="0"
                              max="180"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Depassement tolere (min)</label>
                            <input
                              type="number"
                              value={groupeSelectionne.tolerance_fin_apres_minutes ?? 10}
                              onChange={(e) => handleChangeGroupe('tolerance_fin_apres_minutes', parseInt(e.target.value) || 0)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                              min="0"
                              max="180"
                            />
                          </div>
                        </div>

                        {/* Pause du groupe — n'a de sens que si les pauses se badgent.
                            Vide = herite de l'etablissement, comme NULL en base. */}
                        {parametres.mode_pause === 'pointage' && (() => {
                          const debutHerite = parametres.pause_debut_prevue;
                          const dureeHeritee = parametres.pause_duree_prevue_minutes ?? 60;
                          const margeHeritee = parametres.pause_tolerance_minutes ?? 10;

                          const debut = groupeSelectionne.pause_debut_prevue ?? debutHerite;
                          const duree = groupeSelectionne.pause_duree_prevue_minutes ?? dureeHeritee;
                          const marge = groupeSelectionne.pause_tolerance_minutes ?? margeHeritee;

                          const proprePartout =
                            groupeSelectionne.pause_debut_prevue == null &&
                            groupeSelectionne.pause_duree_prevue_minutes == null &&
                            groupeSelectionne.pause_tolerance_minutes == null;

                          return (
                            <div className="border-t border-gray-200 pt-4 mt-4">
                              <div className="flex items-center justify-between gap-3 mb-1">
                                <div className="flex items-center gap-2">
                                  <Coffee size={18} className="text-[#319190]" />
                                  <label className="block text-sm font-medium text-gray-700">
                                    Pause du groupe
                                  </label>
                                </div>
                                {!proprePartout && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleChangeGroupe('pause_debut_prevue', null);
                                      handleChangeGroupe('pause_duree_prevue_minutes', null);
                                      handleChangeGroupe('pause_tolerance_minutes', null);
                                    }}
                                    className="text-xs text-[#3084b5] hover:underline"
                                  >
                                    Revenir aux horaires de l'établissement
                                  </button>
                                )}
                              </div>

                              <p className="text-xs text-gray-600 mb-3 pl-6">
                                Laissez vide pour suivre l'établissement. Utile quand la cantine
                                et l'entretien ne déjeunent pas à la même heure.
                              </p>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Début de pause
                                  </label>
                                  <input
                                    type="time"
                                    value={groupeSelectionne.pause_debut_prevue?.substring(0, 5) || ''}
                                    onChange={(e) => handleChangeGroupe('pause_debut_prevue', e.target.value ? e.target.value + ':00' : null)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-transparent"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {groupeSelectionne.pause_debut_prevue
                                      ? 'Propre au groupe'
                                      : `Établissement : ${debutHerite?.substring(0, 5) || '—'}`}
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Durée (min)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="720"
                                    value={groupeSelectionne.pause_duree_prevue_minutes ?? ''}
                                    placeholder={String(dureeHeritee)}
                                    onChange={(e) => handleChangeGroupe('pause_duree_prevue_minutes', e.target.value === '' ? null : parseInt(e.target.value, 10) || 0)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-transparent"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {groupeSelectionne.pause_duree_prevue_minutes == null
                                      ? `Établissement : ${dureeHeritee} min`
                                      : 'Propre au groupe'}
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Marge (min)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="180"
                                    value={groupeSelectionne.pause_tolerance_minutes ?? ''}
                                    placeholder={String(margeHeritee)}
                                    onChange={(e) => handleChangeGroupe('pause_tolerance_minutes', e.target.value === '' ? null : parseInt(e.target.value, 10) || 0)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-transparent"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    {groupeSelectionne.pause_tolerance_minutes == null
                                      ? `Établissement : ${margeHeritee} min`
                                      : 'Propre au groupe'}
                                  </p>
                                </div>
                              </div>

                              <p className="mt-3 text-xs leading-relaxed text-gray-600">
                                Ce groupe badge sa sortie de pause dès{' '}
                                <span className="font-mono font-semibold text-gray-800">{decaler(debut, -marge)}</span>,
                                repart à{' '}
                                <span className="font-mono font-semibold text-gray-800">{debut?.substring(0, 5) || '—'}</span>,
                                revient pour{' '}
                                <span className="font-mono font-semibold text-gray-800">{decaler(debut, duree)}</span>
                                {' '}et le retour reste toléré jusqu'à{' '}
                                <span className="font-mono font-semibold text-gray-800">{decaler(debut, duree + marge)}</span>.
                              </p>
                            </div>
                          );
                        })()}


                        {/* Jours travaillés */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Jours travaillés</label>
                          <div className="flex flex-wrap gap-2">
                            {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map(jour => (
                              <label
                                key={jour}
                                className={`px-3 py-2 text-sm rounded-lg cursor-pointer transition-all border-2 ${
                                  groupeSelectionne.jours_travailles?.includes(jour)
                                    ? 'bg-[#3084b5] border-[#3084b5] text-white'
                                    : 'bg-white border-gray-300 text-gray-700'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={groupeSelectionne.jours_travailles?.includes(jour) || false}
                                  onChange={() => handleJourToggleGroupe(jour)}
                                  className="hidden"
                                />
                                <span className="capitalize font-medium">{jour.substring(0,3)}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Couleur */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Couleur</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={groupeSelectionne.couleur || '#3084b5'}
                              onChange={(e) => handleChangeGroupe('couleur', e.target.value)}
                              className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                            />
                            <span className="text-sm text-gray-600">{groupeSelectionne.couleur}</span>
                          </div>
                        </div>


                        {/* Jours spéciaux */}
                        <div className="border-t border-gray-200 pt-4 mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Calendar size={18} className="text-amber-600" />
                            <label className="block text-sm font-medium text-gray-700">
                            Jours spéciaux (horaires différents)
                            </label>
                        </div>
                        
                        <p className="text-xs text-gray-600 mb-3 pl-6">
                            Configurez des horaires de fin différents pour certains jours de la semaine
                        </p>
                        
                        {/* Liste des jours spéciaux existants */}
                        {groupeSelectionne.jours_speciaux && Object.keys(groupeSelectionne.jours_speciaux).length > 0 && (
                            <div className="mb-4 space-y-2">
                            {Object.entries(groupeSelectionne.jours_speciaux).map(([jour, config]) => (
                                <div
                                key={jour}
                                className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                                >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                                    <Calendar size={16} className="text-white" />
                                    </div>
                                    <div>
                                    <span className="font-semibold text-amber-900 capitalize text-sm">{jour}</span>
                                    {config.heure_fin && (
                                      <div className="text-xs text-amber-700">
                                        Fin à {config.heure_fin.substring(0,5)}
                                        <span className="text-amber-600 ml-2">
                                        (au lieu de {groupeSelectionne.heure_fin?.substring(0,5)})
                                        </span>
                                      </div>
                                    )}
                                    {(config.pause_debut || config.pause_duree_minutes !== undefined) && (
                                      <div className="text-xs text-[#246990]">
                                        {config.pause_duree_minutes === 0
                                          ? 'Sans pause ce jour-là'
                                          : <>Pause{config.pause_debut ? ` à ${config.pause_debut.substring(0, 5)}` : ''}
                                             {config.pause_duree_minutes !== undefined ? ` pendant ${config.pause_duree_minutes} min` : ''}</>}
                                      </div>
                                    )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleSupprimerJourSpecial(jour)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                    title="Supprimer ce jour spécial"
                                >
                                    <Trash2 size={16} />
                                </button>
                                </div>
                            ))}
                            </div>
                        )}

                        {/* Formulaire d'ajout */}
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-amber-200">
                            <div className="flex items-center gap-2 mb-3">
                            <Plus size={16} className="text-amber-700" />
                            <span className="text-sm font-semibold text-amber-900">Ajouter un jour spécial</span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Jour de la semaine</label>
                                <ChampSelect
                                  valeur={jourSpecialTemp.jour}
                                  onChange={(valeur) => setJourSpecialTemp(prev => ({ ...prev, jour: valeur }))}
                                  placeholder="Choisir un jour"
                                  options={[
                                    { valeur: 'lundi', libelle: 'Lundi' },
                                    { valeur: 'mardi', libelle: 'Mardi' },
                                    { valeur: 'mercredi', libelle: 'Mercredi' },
                                    { valeur: 'jeudi', libelle: 'Jeudi' },
                                    { valeur: 'vendredi', libelle: 'Vendredi' },
                                    { valeur: 'samedi', libelle: 'Samedi' },
                                    { valeur: 'dimanche', libelle: 'Dimanche' },
                                  ]}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Heure de fin spéciale</label>
                                <input
                                type="time"
                                value={jourSpecialTemp.heure_fin}
                                onChange={(e) => setJourSpecialTemp(prev => ({ ...prev, heure_fin: e.target.value }))}
                                placeholder="Ex: 14:00"
                                className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Pause à (facultatif)</label>
                                <input
                                type="time"
                                value={jourSpecialTemp.pause_debut}
                                onChange={(e) => setJourSpecialTemp(prev => ({ ...prev, pause_debut: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Pendant (min)</label>
                                <input
                                type="number"
                                min="0"
                                max="720"
                                value={jourSpecialTemp.pause_duree_minutes}
                                onChange={(e) => setJourSpecialTemp(prev => ({ ...prev, pause_duree_minutes: e.target.value }))}
                                placeholder="habituelle"
                                className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                                <p className="mt-1 text-[11px] text-amber-700">
                                  0 = pas de pause ce jour-là
                                </p>
                            </div>
                            </div>
                            
                            <Button
                            onClick={handleAjouterJourSpecial}
                            disabled={!jourSpecialTemp.jour
                              || (!jourSpecialTemp.heure_fin && !jourSpecialTemp.pause_debut
                                  && jourSpecialTemp.pause_duree_minutes === '')}
                            size="sm"
                            className="h-9 w-full justify-center gap-2 bg-amber-500 text-white hover:bg-amber-600 disabled:bg-gray-300"
                            >
                            <Plus size={14} className="mr-2" />
                            Ajouter ce jour spécial
                            </Button>
                            
                            {(!jourSpecialTemp.jour
                              || (!jourSpecialTemp.heure_fin && !jourSpecialTemp.pause_debut
                                  && jourSpecialTemp.pause_duree_minutes === '')) && (
                            <p className="text-xs text-amber-600 mt-2 text-center">
                                Choisissez un jour, puis une heure de fin ou un réglage de pause
                            </p>
                            )}
                        </div>
                        
                        {/* Note explicative */}
                        {!groupeSelectionne.jours_speciaux || Object.keys(groupeSelectionne.jours_speciaux).length === 0 ? (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-blue-700">
                                <strong>Exemple d'usage :</strong> Si tous les mercredis votre établissement termine à 14h00 au lieu de l'horaire normal, ajoutez "Mercredi" comme jour spécial avec l'heure 14:00.
                                </div>
                            </div>
                            </div>
                        ) : null}
                        </div>

                        {/* Bouton Enregistrer */}
                        <Button
                          onClick={handleModifierGroupe}
                          disabled={saving}
                          className="w-full h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Enregistrement...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4" />
                              Enregistrer les modifications
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-12 border border-gray-200 text-center">
                      <ClipboardList className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Sélectionnez un groupe pour le configurer</p>
                    </div>
                  )}
                </div>

                {/* ========================================== */}
                {/* COLONNE 3 : EMPLOYÉS (4/12) */}
                {/* ========================================== */}
                <div className="col-span-12 lg:col-span-4">
                  {groupeSelectionne ? (
                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">Employés</h3>

                        <Button
                        onClick={handleAffecterEmployes}
                        disabled={saving}
                        size="sm"
                        className="h-9 justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
                        >
                        <CheckCircle size={14} className="mr-1" />
                        {employesSelectionnes.length > 0 
                            ? `Appliquer (${employesSelectionnes.length})`
                            : 'Retirer tous'
                        }
                        </Button>

                      </div>

                      {/* Recherche */}
                      <div className="mb-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Liste des employés */}
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {/* Employés du groupe */}
                        {employesGroupe.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Dans le groupe</p>
                            {employesGroupe
                              .filter(e => 
                                `${e.nom} ${e.prenom}`.toLowerCase().includes(searchTerm.toLowerCase())
                              )
                              .map(employe => (
                                <label
                                  key={employe.id}
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={employesSelectionnes.includes(employe.id)}
                                    onChange={() => handleToggleEmploye(employe.id)}
                                    className="w-4 h-4 text-[#3084b5] border-gray-300 rounded focus:ring-[#3084b5]"
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {employe.nom} {employe.prenom}
                                    </div>
                                    <div className="text-xs text-gray-500">{employe.role_nom}</div>
                                  </div>
                                </label>
                              ))}
                          </div>
                        )}

                        {/* Employés non affectés */}
                        {employesNonAffectes.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Non affectés</p>
                            {employesNonAffectes
                              .filter(e => 
                                `${e.nom} ${e.prenom}`.toLowerCase().includes(searchTerm.toLowerCase())
                              )
                              .map(employe => (
                                <label
                                  key={employe.id}
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={employesSelectionnes.includes(employe.id)}
                                    onChange={() => handleToggleEmploye(employe.id)}
                                    className="w-4 h-4 text-[#3084b5] border-gray-300 rounded focus:ring-[#3084b5]"
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {employe.nom} {employe.prenom}
                                    </div>
                                    <div className="text-xs text-gray-500">{employe.role_nom}</div>
                                  </div>
                                </label>
                              ))}
                          </div>
                        )}

                        {employesGroupe.length === 0 && employesNonAffectes.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">Aucun employé disponible</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-12 border border-gray-200 text-center">
                      <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Sélectionnez un groupe pour voir les employés</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ========================================== */}
          {/* ONGLET 4 : BADGES RFID (LECTURE SEULE) */}
          {/* ========================================== */}
          {/* Qui porte un badge, sans ouvrir trente fiches dans Ressources
              humaines. Aucun réglage, aucune écriture : le badge s'attribue
              depuis la fiche employé, qui reste le seul chemin d'écriture.
              D'où l'absence de régime d'enregistrement pour cet onglet — le
              filet amber du bas reste donc celui des horaires, et c'est voulu :
              il est là pour rattraper précisément le changement d'onglet. */}
          <TabsContent value="badges-rfid" className="mt-0 focus-visible:outline-none">
            <BadgesRfid />
          </TabsContent>

          {/* Lecture seule, comme les badges : aucun régime d'enregistrement.
              Le rapport n'est calculé qu'à l'ouverture de l'onglet (Radix ne
              monte pas un onglet fermé) : il lit toute la période, et la page
              s'ouvre sur les horaires. */}
          <TabsContent value="rapports" className="mt-0 focus-visible:outline-none">
            <RapportsPointage />
          </TabsContent>

          {/* Le filet du bas : les boutons sont maintenant en en-tete d'onglet,
              et sur une page de cette longueur ils sortent vite du champ. Ce
              rappel n'apparait que s'il y a du non-enregistre — un bandeau
              permanent ne dirait rien. */}
          {activeTab !== 'gestion-horaires' && hasChanges && (
            <div className="mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <AlertCircle size={15} className="text-amber-600" />
              </div>
              <p className="text-xs font-semibold text-amber-700">
                Vous avez des modifications non enregistrées.
              </p>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="ml-auto flex items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white transition-all hover:bg-amber-700 disabled:opacity-60"
              >
                {saving
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Save size={13} />}
                {saving ? 'Enregistrement…' : 'Enregistrer maintenant'}
              </button>
            </div>
          )}

        </div>
      </Tabs>

      <ConfirmDialog
        isOpen={Boolean(groupeASupprimer)}
        onClose={() => setGroupeASupprimer(null)}
        onConfirm={() => {
          const cible = groupeASupprimer;
          setGroupeASupprimer(null);
          if (cible) handleSupprimerGroupe(cible.id);
        }}
        title="Supprimer le groupe horaire"
        description={`Êtes-vous sûr de vouloir supprimer « ${groupeASupprimer?.nom || ''} » ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />

      {/* Info Box */}

      {/* ========================================== */}
      {/* MODAL CRÉATION GROUPE */}
      {/* ========================================== */}
      {/* Le modal etait un calque monte a la main. CLAUDE.md impose les
          primitives `Dialog` : Radix pose `pointer-events: none` sur le body
          tant qu'un Dialog est ouvert, et un overlay maison rendu hors de son
          portail s'affiche mais ne recoit aucun clic. */}
      <Dialog open={showModalGroupe} onOpenChange={setShowModalGroupe}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ClipboardList size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Nouveau groupe d'horaires
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Une équipe qui partage les mêmes heures d'arrivée et de départ
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du groupe *</label>
                  <input
                    type="text"
                    value={nouveauGroupe.nom}
                    onChange={(e) => setNouveauGroupe(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Ex: Personnel d'ouverture"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={nouveauGroupe.description}
                    onChange={(e) => setNouveauGroupe(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ex: Gardiens et personnel d'accueil"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                    rows="2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heure début *</label>
                    <input
                      type="time"
                      value={nouveauGroupe.heure_debut.substring(0,5)}
                      onChange={(e) => setNouveauGroupe(prev => ({ ...prev, heure_debut: e.target.value + ':00' }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heure fin *</label>
                    <input
                      type="time"
                      value={nouveauGroupe.heure_fin.substring(0,5)}
                      onChange={(e) => setNouveauGroupe(prev => ({ ...prev, heure_fin: e.target.value + ':00' }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Debut avant heure prevue (min)</label>
                    <input
                      type="number"
                      value={nouveauGroupe.tolerance_debut_avant_minutes}
                      onChange={(e) => handleNouveauGroupeToleranceChange('tolerance_debut_avant_minutes', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Retard tolere (min)</label>
                    <input
                      type="number"
                      value={nouveauGroupe.tolerance_debut_apres_minutes}
                      onChange={(e) => handleNouveauGroupeToleranceChange('tolerance_debut_apres_minutes', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Depart anticipe tolere (min)</label>
                    <input
                      type="number"
                      value={nouveauGroupe.tolerance_fin_avant_minutes}
                      onChange={(e) => handleNouveauGroupeToleranceChange('tolerance_fin_avant_minutes', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Depassement tolere (min)</label>
                    <input
                      type="number"
                      value={nouveauGroupe.tolerance_fin_apres_minutes}
                      onChange={(e) => handleNouveauGroupeToleranceChange('tolerance_fin_apres_minutes', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3084b5] focus:border-transparent"
                      min="0"
                      max="180"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Couleur</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={nouveauGroupe.couleur}
                      onChange={(e) => setNouveauGroupe(prev => ({ ...prev, couleur: e.target.value }))}
                      className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">{nouveauGroupe.couleur}</span>
                  </div>
                </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModalGroupe(false)}
              className="h-9 min-w-[168px] justify-center gap-2"
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>
            <Button
              onClick={handleCreerGroupe}
              disabled={!nouveauGroupe.nom || !nouveauGroupe.heure_debut || !nouveauGroupe.heure_fin || saving}
              className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {saving ? 'Création…' : 'Créer le groupe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast.show && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ show: false, type: '', message: '' })}
        />
      )}
    </div>
  );
};

export default ParametresPointage;
